import { Button, Flex } from '@cad-arc';
import { useDrawerControls } from '../../../../drawers/components/drawer-context-provider';
import { useMapStoreDataForSelfAssignmentModal } from '../../../../fr-event-modals/use-map-store-data-for-self-assignment-modal';
import { FrAssignButton } from '../../../../fr-event-common-components/fr-assign-button';
import { OpenSensorGridButton } from '../../../../sensors/open-sensor-grid-button';

type EventFooterSectionProps = {
    showManageUnitsButtonInFooter: boolean;
    showAssignSelfButtonInFooter: boolean;
    /** Whether the sensor grid button, for the event's location, joins the footer. */
    showOpenSensorGridButton: boolean;
    hasAssignedUnits: boolean;
    entityId?: number;
    /** The event's location, for the sensor grid. */
    latitude?: number;
    longitude?: number;
};

// Each button fills the footer on its own.
const FILL_FOOTER_STYLE = { width: 'inherit' };
// Beside the sensor grid button they share a row instead: `width: inherit` resolves to the row's
// own full width, so two such buttons overflow it.
const SHARE_ROW_STYLE = { flexGrow: 1, flexBasis: 0, minWidth: 0 };

export function EventFooterSection({
    showManageUnitsButtonInFooter,
    showAssignSelfButtonInFooter,
    showOpenSensorGridButton,
    hasAssignedUnits,
    entityId,
    latitude,
    longitude,
}: EventFooterSectionProps): React.ReactElement {
    const drawerControls = useDrawerControls();
    const { selectedEvent, assignedEvent } = useMapStoreDataForSelfAssignmentModal();

    const handleManageUnits = () => {
        if (entityId) {
            drawerControls.open({
                type: 'manage-units',
                props: {
                    eventId: entityId,
                },
            });
        }
    };

    const buttonStyle = showOpenSensorGridButton ? SHARE_ROW_STYLE : FILL_FOOTER_STYLE;
    const buttons = (
        <>
            {showManageUnitsButtonInFooter && (
                <Button
                    style={buttonStyle}
                    data-testid="MAP_MANAGE_UNITS_BUTTON"
                    onClick={handleManageUnits}
                >
                    Manage Units
                </Button>
            )}
            {showAssignSelfButtonInFooter && (
                <FrAssignButton
                    style={buttonStyle}
                    hasAssignedUnits={hasAssignedUnits}
                    selectedEvent={selectedEvent}
                    assignedEvent={assignedEvent}
                />
            )}
        </>
    );

    // Without the sensor grid button the footer is exactly what it was before sensors.
    if (!showOpenSensorGridButton) {
        return buttons;
    }

    return (
        // The footer is a fixed-height centred row, so the buttons need their own gap.
        <Flex gap={2} width="100%" alignItems="center">
            {buttons}
            <OpenSensorGridButton latitude={latitude} longitude={longitude} />
        </Flex>
    );
}
